import React, { useMemo } from 'react';
import { ArrowLeft, FileText } from 'lucide-react';
import MarkdownRenderer from '../ui/MarkdownRenderer';

interface SavedReportSection {
    type?: 'heading' | 'paragraph' | 'list' | 'table';
    content?: string | string[] | {
        headers?: string[];
        rows?: string[][];
    };
    level?: number;
    ordered?: boolean;
}

interface SavedReport {
    title: string;
    content?: string | {
        title?: string;
        markdown?: string;
        executive_summary?: string;
        sections?: SavedReportSection[];
    };
    references?: Array<{
        title?: string;
        summary?: string;
        document?: string;
        document_reference?: string;
    }>;
}

interface SavedReportViewProps {
    report: SavedReport;
    backLabel: string;
    onBack: () => void;
}

function tableCell(value: unknown): string {
    // Table delimiters and line breaks are the only characters that need
    // normalization here; intentional inline markdown remains renderable.
    return String(value ?? '').replace(/\|/g, '\\|').replace(/\r?\n/g, ' ');
}

function withoutDuplicateLeadingTitle(markdown: string, reportTitle: string): string {
    const lines = markdown.replace(/\r\n/g, '\n').split('\n');
    const firstContentIndex = lines.findIndex((line) => line.trim().length > 0);
    if (firstContentIndex === -1) return '';

    const heading = lines[firstContentIndex].match(/^#\s+(.+?)\s*#*\s*$/);
    if (!heading || heading[1].trim() !== reportTitle.trim()) {
        return lines.join('\n').trim();
    }

    const contentStartIndex = lines.findIndex(
        (line, index) => index > firstContentIndex && line.trim().length > 0,
    );
    return contentStartIndex === -1 ? '' : lines.slice(contentStartIndex).join('\n').trim();
}

export function savedReportToMarkdown(report: SavedReport): string {
    const lines: string[] = [];
    const content = report.content;

    if (typeof content === 'string') {
        lines.push(withoutDuplicateLeadingTitle(content, report.title));
        lines.push('');
    } else if (content) {
        // Inventory and newer saved-report producers persist the finished
        // document as markdown; older report producers use structured sections.
        // Support both server shapes without guessing from the report id.
        if (content.markdown?.trim()) {
            lines.push(withoutDuplicateLeadingTitle(content.markdown, report.title), '');
        } else {
            if (content.executive_summary) {
                lines.push('## Executive Summary', '', content.executive_summary, '');
            }

            content.sections?.forEach((section) => {
                if (section.type === 'heading' && typeof section.content === 'string') {
                    // The view owns the report H1, so stored level 1 begins at
                    // H2 and every producer level is clamped to H2-H6.
                    const prefix = '#'.repeat(Math.min(Math.max((section.level ?? 1) + 1, 2), 6));
                    lines.push(`${prefix} ${section.content}`, '');
                    return;
                }
                if (section.type === 'paragraph' && typeof section.content === 'string') {
                    lines.push(section.content, '');
                    return;
                }
                if (section.type === 'list' && Array.isArray(section.content)) {
                    section.content.forEach((item, index) => {
                        lines.push(section.ordered ? `${index + 1}. ${item}` : `- ${item}`);
                    });
                    lines.push('');
                    return;
                }
                if (
                    section.type === 'table'
                    && section.content
                    && !Array.isArray(section.content)
                    && typeof section.content === 'object'
                ) {
                    const headers = section.content.headers ?? [];
                    if (headers.length > 0) {
                        lines.push(`| ${headers.map(tableCell).join(' | ')} |`);
                        lines.push(`| ${headers.map(() => '---').join(' | ')} |`);
                    }
                    section.content.rows?.forEach((row) => {
                        lines.push(`| ${row.map(tableCell).join(' | ')} |`);
                    });
                    lines.push('');
                }
            });
        }
    }

    const references = report.references?.filter((reference) => reference.title) ?? [];
    if (references.length > 0) {
        lines.push('## References', '');
        references.forEach((reference, index) => {
            lines.push(`${index + 1}. **${reference.title}**`);
            if (reference.document || reference.document_reference) {
                lines.push(`   ${[reference.document, reference.document_reference].filter(Boolean).join(' — ')}`);
            }
            if (reference.summary) {
                lines.push(`   ${reference.summary}`);
            }
            lines.push('');
        });
    }

    return lines.join('\n').trim();
}

export default function SavedReportView({ report, backLabel, onBack }: SavedReportViewProps) {
    const markdown = useMemo(() => savedReportToMarkdown(report), [report]);

    return (
        <section className="h-full flex flex-col bg-bg-warm" data-testid="saved-report-view">
            <header className="flex-shrink-0 border-b border-border-default bg-surface px-6 py-4">
                <button
                    type="button"
                    onClick={onBack}
                    className="mb-3 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-fg-secondary hover:bg-bg-warm hover:text-fg focus-visible:outline-none focus-visible:shadow-bark-focus"
                >
                    <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
                    {backLabel}
                </button>
                <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-md bg-accent-tint p-2 text-accent">
                        <FileText className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs font-medium text-fg-tertiary">Saved report</p>
                        <h1 className="mt-0.5 text-xl font-semibold text-fg">{report.title}</h1>
                    </div>
                </div>
            </header>
            <div className="flex-1 overflow-y-auto p-6">
                <article className="mx-auto max-w-4xl rounded-lg border border-border-default bg-surface px-8 py-7 shadow-bark-xs">
                    {markdown ? (
                        // Raw HTML remains escaped because MarkdownRenderer
                        // intentionally does not install rehype-raw.
                        <MarkdownRenderer content={markdown} className="text-fg-secondary" gfm />
                    ) : (
                        <p className="text-sm text-fg-tertiary" role="status">
                            This report has no displayable content.
                        </p>
                    )}
                </article>
            </div>
        </section>
    );
}
